import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sendEvent } from 'utils/Event';
import CustomDataGridResult from 'utils/Datagrid/CustomForecastDataGridResult';
import {
  CustomCropToPlantHeader,
  CustomEditBrushAutomaticEffectCell,
  CustomEffectEditCell,
  brushValueList,
  customCropToPlantCell,
  handleRowUpdateValidation,
} from 'utils/Datagrid/CustomRotateMatricesDataGridUtils';
import { inputFloatValidatorRotateEffect } from 'components/generics/Validators/validator';
import {
  checkThreeValueAndDecimalRegexTwoDigitsDatagrid,
  displayErrorMessage,
  getNewKeyBetweenObjects,
} from 'utils/tools_functions';
import './dataGridRotateEffects.style.scss';
import { toast } from 'react-toastify';
import { updateRotationEffect } from 'services/API/Cooperative';
import { Button, ThemeProvider } from '@mui/material';
import { validateDatagridMatrix } from 'components/generics/Validators/datagrid.validate';
import useFilteredRotationsLocalStorage from 'utils/Datagrid/useFilteredRotationsLocalStorage';
import useDataGridMatricesCellBehaviour from 'utils/Datagrid/useDataGridMatricesCellBehaviour';
import useDataGridMatricesBrushMode from 'utils/Datagrid/useDataGridMatricesBrushMode';
import useToggleSwitch from 'utils/hooks/useToggleSwitch';
import { CustomTooltipEffects } from 'utils/Datagrid/Effects/CustomEffectsDataGridUtils';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import brushLogo from 'assets/images/brush.svg';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import { CustomToolbarEffects } from '../../../utils/Datagrid/Effects/CustomEffectsDataGridUtils';
import { rotateEffectTheme } from 'utils/Datagrid/CustomForecastDataGridUtils';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { createCustomRotateEffectsFilterPanel } from 'utils/Datagrid/CustomFilterRotateEffects';
import useNewFilteredRotationLocalStorage from 'utils/Datagrid/useNewFilteredRotationLocalStorage';

const DataGridRotateEffects = ({ cultures, refreshData, ownerId, from }) => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [fullScreen, setFullScreen] = useState(false);
  const [currentRotationLevel, setCurrentRotationLevel] = useState('cultureN1');
  const [allowSubmitEffect, setAllowSubmitEffect] = useState(false);
  const stackedRequestsRef = useRef([]);
  const previousRotationLevel = useRef('cultureN1');
  const [isLoading, setIsLoading] = useState(false);
  const [errorsEffectFormDefault, setErrorsEffectFormDefault] = useState({
    formError: false,
    effect: {
      message: '',
      validator: [inputFloatValidatorRotateEffect],
    },
    cellErrors: { cultureN1: {}, cultureN2: {} },
  });
  const [rotationLevelToggle, setRotationLevelToggle] = useState({
    cultureN1: false,
    cultureN2: false,
  });
  const [editedRows, setEditedRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [hasClickAway, setHasClickAway] = useState(true);
  const apiRef = useGridApiRef();
  const handleOpenFilter = () => {
    apiRef.current.showFilterPanel();
  };

  const { renderToggleSwitch, isOrganic } = useToggleSwitch(
    from,
    'rotateEffects',
    refreshData,
    ownerId,
    stackedRequestsRef,
    errorsEffectFormDefault?.formError,
  );

  const stateIsOrganic = isOrganic ? 'organic' : 'conventional';
  const { t } = useTranslation();

  const { activeFilters, selectedFilters, updateFilters, resetFilters } =
    useNewFilteredRotationLocalStorage(
      isOrganic,
      stateIsOrganic,
      Number(ownerId),
      from,
      cultures,
      'rotationEffects',
    );

  const { renderCell, onCellEditStop, rowModesModel, setRowModesModel, handleRowModesModelChange } =
    useDataGridMatricesCellBehaviour({
      stackedRequestsRef,
      errorsEffectFormDefault,
      setErrorsEffectFormDefault,
      currentRotationLevel,
      validateDatagridMatrix,
      hasClickAway,
      setHasClickAway,
    });

  const {
    modeBrush,
    setModeBrush,
    allowSubmitEffectBrush,
    setAllowSubmitEffectBrush,
    selectedBrushValue,
    setSelectedBrushValue,
    handleBrushEffect,
    handleClickValue,
  } = useDataGridMatricesBrushMode();

  useEffect(() => {
    // Dismount
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  const processedRows = useMemo(() => {
    return cultures?.map((cultureInRow) => {
      let processedRow = {
        id: cultureInRow.id,
        cropToPlant: cultureInRow.name,
        rotationEffects: cultureInRow.rotationEffects,
        cultureType: cultureInRow.cultureType,
      };

      return processedRow;
    });
  }, [cultures]);

  useEffect(() => {
    setIsLoading(true);
    const filteredRows = processedRows?.filter((culture) => {
      return selectedFilters?.[stateIsOrganic]?.rows?.[culture.id] !== false;
    });
    const filteredColumns = processedRows?.filter((culture) => {
      return selectedFilters?.[stateIsOrganic]?.columns?.[culture.id] !== false;
    });
    if (
      filteredRows?.length !== processedRows?.length ||
      filteredColumns?.length !== processedRows?.length
    ) {
      processedRows && setRows(filteredRows);
      processedRows && setColumns(filteredColumns);
    } else {
      processedRows && setRows(processedRows);
      processedRows && setColumns(processedRows);
    }
    setIsLoading(false);
  }, [cultures, stateIsOrganic, selectedFilters]);

  useEffect(() => {
    if (processedRows && !isLoading) {
      const filteredRows = processedRows?.filter(
        (row) => selectedFilters?.[stateIsOrganic]?.rows?.[row.id],
      );
      const columnsToFilter = filteredColumns?.filter(
        (column) => selectedFilters?.[stateIsOrganic]?.columns?.[column.id],
      );
      const updatedFilteredRows = filteredRows.map((row) => {
        if (editedRows[row.id]) {
          return { ...row, ...editedRows[row.id] };
        }
        return row;
      });
      setRows(updatedFilteredRows || []);
      setColumns(columnsToFilter || []);
    }
  }, [selectedFilters, editedRows, stateIsOrganic]);

  const renderEditBrushAutomaticEffectCell = (params) => {
    return (
      <CustomEditBrushAutomaticEffectCell
        selectedBrushValue={selectedBrushValue}
        params={params}
        previousRotationLevel={previousRotationLevel}
        currentRotationLevel={currentRotationLevel}
      />
    );
  };

  const renderEditEffectCell = (params) => {
    return (
      <CustomEffectEditCell
        params={params}
        errorsEffectFormDefault={errorsEffectFormDefault}
        currentRotationLevel={currentRotationLevel}
        setRowModesModel={setRowModesModel}
        previousRotationLevel={previousRotationLevel}
        setHasClickAway={setHasClickAway}
      />
    );
  };

  const filteredColumns = processedRows && [...processedRows];

  const processColumns = columns?.map((rowFromColumns) => {
    const isIntermediate =
      cultures?.find((culture) => culture.id === rowFromColumns.id)?.cultureType === 'intermediate';

    return {
      field: rowFromColumns.id.toString(),
      headerName: rowFromColumns.cropToPlant,

      headerClassName: isIntermediate
        ? 'cropEffect-header cropEffect-header--intermediate'
        : 'cropEffect-header',
      editable: true,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      type: 'number',
      minWidth: 80,
      cellClassName: 'viewModeCell',
      renderCell: renderCell,
      renderEditCell:
        modeBrush && selectedBrushValue !== null
          ? renderEditBrushAutomaticEffectCell
          : renderEditEffectCell,
      valueGetter: (value, row) => {
        return row?.rotationEffects.find(
          (rotationEffect) => rotationEffect.cultureId === rowFromColumns.id,
        )?.[currentRotationLevel].effect;
      },
      valueParser: (value) => {
        return checkThreeValueAndDecimalRegexTwoDigitsDatagrid(value);
      },
    };
  });

  const cropToPlantColumn = {
    field: 'cropToPlant',
    headerName: t('column.cropToPlant'),
    headerClassName: 'cropToPlant-header',
    renderHeader: (params) => CustomCropToPlantHeader({ ...params }),
    renderCell: (params) => customCropToPlantCell({ ...params }),
    cellClassName: 'cropToPlant-cell',
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 140,
  };

  const noColumns = {
    field: 'noColumns',
    headerName: '',
    headerClassName: 'cropEffect-header',
    renderCell: (params) => {
      if (params.row.id === rows[0]?.id) {
        return <div className="noColumns">{t('label.noColumnsToDisplay')}</div>;
      }
      return null;
    },
    cellClassName: 'viewModeCell-noColumns',
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 140,
  };

  const processedColumns = [
    cropToPlantColumn,
    ...(processColumns.length > 0 ? processColumns : [noColumns]),
  ];

  const processRowUpdate = useCallback(
    async (updatedRow, previousRow) => {
      let rotationLevel = currentRotationLevel;
      let switchingTab = false;
      if (previousRotationLevel.current !== currentRotationLevel) {
        rotationLevel = previousRotationLevel.current;
        switchingTab = true;
      }
      errorsEffectFormDefault.formError = false;
      const toUpdate = getNewKeyBetweenObjects(previousRow, updatedRow);
      const previousCrop = Number(Object.keys(toUpdate)[0]);
      const isEffectUpdated =
        previousRow.rotationEffects.find(
          (rotationEffect) => rotationEffect.cultureId === previousCrop,
        )[rotationLevel].effect !== toUpdate[previousCrop];

      const isEffectSameAsOriginalEffect =
        cultures
          .find((culture) => culture.id === updatedRow.id)
          .rotationEffects.find((rotationEffect) => rotationEffect.cultureId === previousCrop)[
          rotationLevel
        ].effect === toUpdate[previousCrop];

      if (isEffectUpdated) {
        const formatedToUpdateObject = { ...toUpdate };
        Object.defineProperty(
          formatedToUpdateObject,
          'effect',
          Object.getOwnPropertyDescriptor(formatedToUpdateObject, previousCrop),
        );
        delete formatedToUpdateObject[previousCrop];

        setErrorsEffectFormDefault({
          ...validateDatagridMatrix(
            formatedToUpdateObject,
            errorsEffectFormDefault,
            updatedRow.id,
            previousCrop,
            rotationLevel,
          ),
        });

        if (switchingTab && errorsEffectFormDefault.formError) {
          setCurrentRotationLevel(previousRotationLevel.current);
          toast.error(t('errors.rotationEffectsInvalidChangeTable'));
        }

        updatedRow = {
          ...updatedRow,
          rotationEffects: updatedRow.rotationEffects.map((rotationEffect) => {
            return rotationEffect.cultureId === previousCrop
              ? {
                  ...rotationEffect,
                  [rotationLevel]: {
                    ...rotationEffect[rotationLevel],
                    effect: toUpdate[previousCrop],
                  },
                }
              : rotationEffect;
          }),
        };
        delete updatedRow[previousCrop];

        const updatedEffect = updatedRow.rotationEffects.find(
          (rotationEffect) => rotationEffect.cultureId === previousCrop,
        );

        let existInStackedRequest = false;
        stackedRequestsRef.current.forEach((request) => {
          if (
            request?.uriParams?.rotateEffectId === updatedEffect[rotationLevel]?.id &&
            request?.cellInformations?.year === rotationLevel
          ) {
            existInStackedRequest = true;
          }
        });

        await handleRowUpdateValidation(updatedRow, errorsEffectFormDefault);

        if (!existInStackedRequest) {
          stackedRequestsRef.current.push({
            uriParams: {
              ownerId: ownerId,
              yearEffect: rotationLevel === 'cultureN1' ? 'n1' : 'n2',
              rotateEffectId: updatedEffect[rotationLevel]?.id,
            },
            body: {
              id: updatedEffect[rotationLevel]?.id,
              from: updatedEffect[rotationLevel]?.from,
              effect: updatedEffect[rotationLevel]?.effect,
              year: rotationLevel === 'cultureN1' ? 'N1' : 'N2',
              isOrganic: isOrganic,
            },
            cellInformations: {
              year: rotationLevel,
              rowId: updatedRow.id,
              columnId: previousCrop,
              rotateEffectId: updatedEffect[rotationLevel]?.id,
              effect: updatedEffect[rotationLevel]?.effect,
            },
          });
        } else if (!isEffectSameAsOriginalEffect) {
          stackedRequestsRef.current = stackedRequestsRef.current.map((request) => {
            if (
              request.uriParams.rotateEffectId === updatedEffect[rotationLevel]?.id &&
              request?.cellInformations?.year === rotationLevel
            ) {
              return {
                ...request,
                body: {
                  ...request.body,
                  effect: updatedEffect[rotationLevel]?.effect,
                },
                cellInformations: {
                  ...request.cellInformations,
                  effect: updatedEffect[rotationLevel]?.effect,
                },
              };
            } else {
              return request;
            }
          });
        } else {
          let tempStackedRequests = [];
          stackedRequestsRef.current.forEach((request) => {
            if (
              request.cellInformations.rotateEffectId !== updatedEffect[rotationLevel]?.id ||
              request.cellInformations.year !== rotationLevel
            ) {
              tempStackedRequests.push(request);
            }
          });
          stackedRequestsRef.current = tempStackedRequests;
        }

        setAllowSubmitEffect(stackedRequestsRef?.current?.length);
        if (modeBrush) {
          setAllowSubmitEffectBrush(true);
        }

        setEditedRows((prev) => ({
          ...prev,
          [updatedRow.id]: updatedRow,
        }));

        return updatedRow;
      }

      return previousRow;
    },
    [cultures, currentRotationLevel, stackedRequestsRef, rows, editedRows],
  );

  const handleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const handleRotationLevel = (level) => {
    previousRotationLevel.current = level === 'cultureN1' ? 'cultureN2' : 'cultureN1';
    setCurrentRotationLevel(level);
  };

  useEffect(() => {
    setRotationLevelToggle((prev) => ({
      ...prev,
      cultureN1: Object.keys(errorsEffectFormDefault?.cellErrors.cultureN1 || {}).length === 0,
    }));
    setRotationLevelToggle((prev) => ({
      ...prev,
      cultureN2: Object.keys(errorsEffectFormDefault?.cellErrors.cultureN2 || {}).length === 0,
    }));
  }, [errorsEffectFormDefault]);

  const validateEffectLevel = (level) => {
    const nextLevel = level === 'cultureN1' ? 'cultureN2' : 'cultureN1';
    const currentToggle = rotationLevelToggle[level];

    if (currentToggle) {
      handleRotationLevel(nextLevel);
    } else {
      toast.warning(
        t('errors.rotationEffectsInvalidLevel', { level: level === 'cultureN1' ? 'N-1' : 'N-2' }),
      );
    }
  };

  const callAPI = async () => {
    setIsLoading(true);
    if (
      Object.keys(errorsEffectFormDefault?.cellErrors?.cultureN1)?.length !== 0 ||
      Object.keys(errorsEffectFormDefault?.cellErrors?.cultureN2)?.length !== 0
    ) {
      toast.warning(t('errors.rotationEffectsNotSaved'));
    }

    // .filter() at the end to clean up the array for undefined/null object
    const requestBody = stackedRequestsRef.current
      .map((request) => request.body)
      .filter((request) => request);

    try {
      await updateRotationEffect(from, ownerId, requestBody);
      setAllowSubmitEffect(false);
      setAllowSubmitEffectBrush(false);
      setModeBrush(false);
      setSelectedBrushValue(null);
      setEditedRows([]);
    } catch (error) {
      toast.error(
        displayErrorMessage(error?.response?.data?.message ?? 'ERR_UPDATE_ROTATION_EFFECT'),
      );
    }
    stackedRequestsRef.current = [];
    refreshData(isOrganic, ownerId, false, 'rotation');
    toast.success(t('success.rotationEffectsUpdated'));
  };

  const handleTooltipToggle = () => {
    setOpen((open) => !open);
  };

  const filterPanelPropsRef = useRef({
    cultures: [],
    stateIsOrganic: '',
    activeFilters: [],
    updateFilters: () => {},
    resetFilters: () => {},
  });

  filterPanelPropsRef.current = {
    ...filterPanelPropsRef.current,
    cultures,
    stateIsOrganic,
    activeFilters,
    updateFilters,
    resetFilters,
  };

  const CustomFilterPanel = useMemo(
    () => createCustomRotateEffectsFilterPanel(filterPanelPropsRef),
    [],
  );

  return (
    cultures && (
      <div className="section">
        <div className="main_container">
          <div className="override">
            {from !== 'exploitations' && (
              <h1 className="title_section title_section_culture title_rotate_effects">
                {t('title.rotationMatrix')}
              </h1>
            )}
          </div>
          <div
            className={`datagrid_container rotate_effects_container ${
              modeBrush ? 'modeBrush' : ''
            }`}
          >
            <div className="contain_tab_rotation">
              <div
                className={`${currentRotationLevel === 'cultureN1' ? 'active' : ''}
                ${rotationLevelToggle.cultureN2 ? '' : 'disabled' ? 'disabled' : ''}
                `}
                onClick={() => {
                  if (currentRotationLevel !== 'cultureN1') {
                    validateEffectLevel('cultureN2');
                  }
                }}
              >
                {t('label.rotationEffectsN1')}
              </div>
              <div
                className={`${currentRotationLevel === 'cultureN2' ? 'active' : ''}
                ${rotationLevelToggle.cultureN1 ? '' : 'disabled' ? 'disabled' : ''}
                `}
                onClick={() => {
                  if (currentRotationLevel !== 'cultureN2') {
                    validateEffectLevel('cultureN1');
                  }
                }}
              >
                {t('label.rotationEffectsN2')}
              </div>
            </div>
            <div className={`${fullScreen ? 'fullscreen' : ''}`}>
              <div
                className={`${
                  fullScreen
                    ? 'contain_brush_effect_fullscreen contain_brush_effect'
                    : 'contain_brush_effect'
                }`}
              >
                {fullScreen && (
                  <div>
                    <ThemeProvider theme={customButtonTheme}>
                      <Button
                        disabled={!allowSubmitEffectBrush}
                        onClick={handleBrushEffect}
                        sx={{ ml: 2 }}
                      >
                        {t('button.finish')}
                        <span>
                          <img src={brushLogo} alt={t('alt.brushLogo')} className="brush_logo" />
                        </span>
                      </Button>
                    </ThemeProvider>
                  </div>
                )}
                {!fullScreen && <div>{renderToggleSwitch()}</div>}
                <div className="filter_container_rotate_effects">
                  <Button startIcon={<FilterListIcon />} onClick={handleOpenFilter}>
                    {t('common.filters').toUpperCase()}
                  </Button>
                </div>
                <CustomTooltipEffects
                  open={open}
                  setOpen={setOpen}
                  handleTooltipToggle={handleTooltipToggle}
                  brushValueList={brushValueList}
                  selectedBrushValue={selectedBrushValue}
                  handleClickValue={handleClickValue}
                  modeBrush={modeBrush}
                  setSelectedBrushValue={setSelectedBrushValue}
                />
                {fullScreen && (
                  <div className="contain_switch_effect handle-full-screen">
                    <ZoomInMapIcon onClick={handleFullScreen} />
                  </div>
                )}
              </div>

              <div>
                <CustomDataGridResult
                  externalApiRef={apiRef}
                  filterPanel={CustomFilterPanel}
                  theme={rotateEffectTheme}
                  rows={rows}
                  getRowClassName={(params) => {
                    const culture = cultures?.find((culture) => culture.id === params.row.id);
                    return culture?.cultureType === 'intermediate' ? 'intermediate' : '';
                  }}
                  fromRotateEffects
                  columns={processedColumns}
                  disableColumnResize={true}
                  processRowUpdate={processRowUpdate}
                  onCellEditStop={onCellEditStop}
                  onRowModesModelChange={handleRowModesModelChange}
                  rowModesModel={rowModesModel}
                  setRowModesModel={setRowModesModel}
                  fullSize={true}
                  stackedRequestsRef={stackedRequestsRef}
                  customToolbar={
                    <CustomToolbarEffects
                      handleFullScreen={handleFullScreen}
                      callAPI={callAPI}
                      allowSubmitEffect={allowSubmitEffect}
                      allowSubmitEffectBrush={allowSubmitEffectBrush}
                      handleBrushEffect={handleBrushEffect}
                      fullScreen={fullScreen}
                      from={from}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default DataGridRotateEffects;
